import { usePricingContext } from '@vp/react-pricing-context';
import { Currency } from '@99designs/design-services-common';
import { KeyValue } from '@99designs/graph-utils/types';
import { ProductOptionsQuery, useProductOptionsPricingQuery } from '../graphql';

function mapProductOptionsToKeyValue(options: ProductOptionsQuery['productOptions']): KeyValue[] {
    return options.reduce<KeyValue[]>((acc, option) => {
        const optionValues = option.values.map((value) => ({
            __typename: 'KeyValue' as const,
            key: option.key.key,
            value: value.key,
        }));

        return [...acc, ...optionValues];
    }, []);
}

type UseProductOptionsPricingParams = {
    productKey: string;
    pimMerchantVersion: number;
    selectedProductOptions: Record<string, string>;
    productOptions: ProductOptionsQuery['productOptions'];
};

export function useProductOptionsPricing({
    productKey,
    pimMerchantVersion,
    selectedProductOptions,
    productOptions,
}: UseProductOptionsPricingParams) {
    const pricingContext = usePricingContext();

    const { data, error, loading } = useProductOptionsPricingQuery({
        variables: {
            input: {
                productKey,
                pimMerchantVersion,
                selections: Object.entries(selectedProductOptions).map(([key, value]) => ({
                    key,
                    value,
                })),
                pivotSelections: mapProductOptionsToKeyValue(productOptions),
            },
        },
    });

    if (error) {
        console.error('Error fetching product options pricing:', error);
    }

    const currency = data?.selectionsPricing.currency as Currency;
    const pivotPricing = data?.selectionsPricing.pivotPrices || [];

    // Default to the highest price (i.e. taxed) while pricingContext loads
    let price = data?.selectionsPricing.listPrice.taxed || 0;
    if (pricingContext && !pricingContext.vatInclusive) {
        price = data?.selectionsPricing.listPrice.untaxed || 0;
    }

    return {
        price,
        currency,
        pivotPricing,
        loading,
        error,
    };
}
